import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  userId: string | undefined;
}

interface SandboxRow {
  status: string;
  last_active_at: string;
}

const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const SEVENTYTWO_HOURS_MS = 72 * 60 * 60 * 1000;

export function SandboxStatusBadge({ userId }: Props) {
  const [row, setRow] = useState<SandboxRow | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!userId) {
      setRow(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("user_sandboxes")
        .select("status, last_active_at")
        .eq("user_id", userId)
        .maybeSingle();
      if (!cancelled) {
        setRow((data as SandboxRow | null) ?? null);
        setNow(Date.now());
      }
    };

    load();
    const interval = setInterval(load, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId]);

  if (!row || row.status === "deleted") return null;
  if (row.status !== "active") return null;

  const lastActive = new Date(row.last_active_at).getTime();
  const elapsed = now - lastActive;

  let dotClass = "";
  let label = "";

  if (elapsed <= FIFTEEN_MIN_MS) {
    dotClass = "bg-green-500 animate-pulse";
    label = "Área de trabalho ativa";
  } else if (elapsed <= SEVENTYTWO_HOURS_MS) {
    dotClass = "bg-yellow-500";
    label = "Área de trabalho hibernando";
  } else {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-secondary">
      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
      <span>{label}</span>
    </div>
  );
}

export default SandboxStatusBadge;
